import { z } from 'zod';
import { buildMetadataSchema } from './compatibility.js';
import { locationMergeDecisionSchema, locationSchema } from './location.js';
import { localProfileSchema, userPreferencesSchema } from './profile.js';
import { immutableCheckpointSchema } from './platform.js';
import { canonicalJsonStringify, decimalCursorSchema, instantSchema, semverSchema, sha256HexBytes, sha256Schema, sha256CanonicalJson, uuidSchema, } from './primitives.js';
import { routeTemplateSchema, routeVariantSchema } from './route.js';
import { shiftAggregateSchema, tripRevisionSchema } from './shift.js';
import { deviceCursorSchema, pendingSyncOperationSchema, syncEntityTypeSchema } from './sync.js';
import { datasetEpochSchema, routeDraftWireGenerationOf } from './versioning.js';
import { vehicleSchema } from './vehicle.js';
export const exportSyncMetadataSchema = z.strictObject({
    pending_outbox: z.array(pendingSyncOperationSchema).max(100_000),
    device_cursor: deviceCursorSchema.nullable(),
    last_server_cursor: decimalCursorSchema.nullable(),
});
export const exportPayloadSchema = z
    .strictObject({
    dataset_epoch: datasetEpochSchema,
    local_profile: localProfileSchema,
    shifts: z.array(shiftAggregateSchema).max(100_000),
    trip_revisions: z.array(tripRevisionSchema).max(1_000_000),
    vehicles: z.array(vehicleSchema).max(10_000),
    route_variants: z.array(routeVariantSchema).max(100_000),
    route_templates: z.array(routeTemplateSchema).max(100_000),
    user_defined_locations: z.array(locationSchema).max(100_000),
    location_merge_decisions: z.array(locationMergeDecisionSchema).max(100_000),
    user_preferences: userPreferencesSchema,
    sync_metadata: exportSyncMetadataSchema,
})
    .superRefine((value, context) => {
    if (value.local_profile.dataset_epoch !== value.dataset_epoch.dataset_epoch) {
        context.addIssue({
            code: 'custom',
            path: ['local_profile', 'dataset_epoch'],
            message: 'profile and export payload must use the same dataset epoch',
        });
    }
    value.shifts.forEach((aggregate, index) => {
        if (aggregate.dataset_epoch !== value.dataset_epoch.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['shifts', index, 'dataset_epoch'],
                message: 'all exported aggregates must use the payload dataset epoch',
            });
        }
    });
    value.trip_revisions.forEach((revision, index) => {
        if (revision.dataset_epoch !== value.dataset_epoch.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['trip_revisions', index, 'dataset_epoch'],
                message: 'all exported revisions must use the payload dataset epoch',
            });
        }
    });
    value.sync_metadata.pending_outbox.forEach((operation, index) => {
        if (operation.dataset_epoch !== value.dataset_epoch.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['sync_metadata', 'pending_outbox', index, 'dataset_epoch'],
                message: 'outbox operation belongs to another dataset epoch',
            });
        }
        if (operation.local_profile_id !== value.local_profile.local_profile_id) {
            context.addIssue({
                code: 'custom',
                path: ['sync_metadata', 'pending_outbox', index, 'local_profile_id'],
                message: 'outbox operation belongs to another local profile',
            });
        }
    });
    if (value.sync_metadata.device_cursor &&
        value.sync_metadata.device_cursor.dataset_epoch !== value.dataset_epoch.dataset_epoch) {
        context.addIssue({
            code: 'custom',
            path: ['sync_metadata', 'device_cursor', 'dataset_epoch'],
            message: 'device cursor belongs to another dataset epoch',
        });
    }
    if (value.sync_metadata.device_cursor) {
        const profileAccount = value.local_profile.account_identity;
        const cursorAccount = value.sync_metadata.device_cursor.account_identity;
        if (profileAccount === null ||
            profileAccount.issuer !== cursorAccount.issuer ||
            profileAccount.subject !== cursorAccount.subject) {
            context.addIssue({
                code: 'custom',
                path: ['sync_metadata', 'device_cursor', 'account_identity'],
                message: 'device cursor must belong to the bound local-profile account',
            });
        }
    }
    value.user_defined_locations.forEach((location, index) => {
        if (location.origin !== 'user_defined') {
            context.addIssue({
                code: 'custom',
                path: ['user_defined_locations', index, 'origin'],
                message: 'official catalog rows are not embedded in personal backups',
            });
        }
    });
    requireUnique(value.shifts.map((item) => item.shift.shift_id), 'shifts', context);
    requireUnique(value.trip_revisions.map((item) => item.trip_revision_id), 'trip_revisions', context);
    requireUnique(value.vehicles.map((item) => item.vehicle_id), 'vehicles', context);
    requireUnique(value.route_variants.map((item) => item.route_variant_id), 'route_variants', context);
    requireUnique(value.route_templates.map((item) => item.route_template_id), 'route_templates', context);
    requireUnique(value.user_defined_locations.map((item) => item.location_id), 'user_defined_locations', context);
    requireUnique(value.location_merge_decisions.map((item) => item.decision_id), 'location_merge_decisions', context);
    requireUnique(value.sync_metadata.pending_outbox.map((item) => item.operation_id), 'sync_metadata.pending_outbox.operation_id', context);
    requireUnique(value.sync_metadata.pending_outbox.map((item) => item.idempotency_key_sha256), 'sync_metadata.pending_outbox.idempotency_key_sha256', context);
    const epoch = value.dataset_epoch.dataset_epoch;
    value.trip_revisions.forEach((revision, index) => {
        validateTombstoneEpoch(revision.before?.metadata.tombstone ?? null, epoch, ['trip_revisions', index, 'before', 'metadata', 'tombstone'], context);
        validateTombstoneEpoch(revision.after?.metadata.tombstone ?? null, epoch, ['trip_revisions', index, 'after', 'metadata', 'tombstone'], context);
    });
    value.vehicles.forEach((record, index) => validateTombstoneEpoch(record.metadata.tombstone, epoch, ['vehicles', index, 'metadata', 'tombstone'], context));
    value.route_variants.forEach((record, index) => validateTombstoneEpoch(record.metadata.tombstone, epoch, ['route_variants', index, 'metadata', 'tombstone'], context));
    value.route_templates.forEach((record, index) => validateTombstoneEpoch(record.metadata.tombstone, epoch, ['route_templates', index, 'metadata', 'tombstone'], context));
    value.user_defined_locations.forEach((record, index) => validateTombstoneEpoch(record.metadata.tombstone, epoch, ['user_defined_locations', index, 'metadata', 'tombstone'], context));
    value.location_merge_decisions.forEach((record, index) => validateTombstoneEpoch(record.metadata.tombstone, epoch, ['location_merge_decisions', index, 'metadata', 'tombstone'], context));
    validateTombstoneEpoch(value.user_preferences.metadata.tombstone, epoch, ['user_preferences', 'metadata', 'tombstone'], context);
    const expandedRouteWirePresent = value.shifts.some((aggregate) => aggregate.segments.some((segment) => tripSegmentUsesExpandedRouteWire(segment))) ||
        value.trip_revisions.some((revision) => (revision.before !== null && tripSegmentUsesExpandedRouteWire(revision.before)) ||
            (revision.after !== null && tripSegmentUsesExpandedRouteWire(revision.after))) ||
        value.route_variants.some((route) => routeUsesExpandedLearningWire(route)) ||
        value.sync_metadata.pending_outbox.some((operation) => {
            const payload = operation.payload;
            if (payload.entity_type === 'shift_aggregate') {
                return payload.value.segments.some((segment) => tripSegmentUsesExpandedRouteWire(segment));
            }
            return (payload.entity_type === 'route_variant' && routeUsesExpandedLearningWire(payload.value));
        });
    if (expandedRouteWirePresent && routeDraftWireGenerationOf(value.dataset_epoch) === null) {
        context.addIssue({
            code: 'custom',
            path: ['dataset_epoch', 'route_draft_wire_generation'],
            message: 'expanded route wire requires its monotonic dataset generation marker',
        });
    }
});
export const CURRENT_EXPORT_SCHEMA_VERSION = '1.0.0';
export const SUPPORTED_EXPORT_SCHEMA_VERSIONS = [CURRENT_EXPORT_SCHEMA_VERSION];
const exportEnvelopeDraftSchema = z.strictObject({
    contract_version: z.literal('export_envelope.v1'),
    schema_version: z.literal(CURRENT_EXPORT_SCHEMA_VERSION),
    canonical_json_version: z.literal('dmf-canonical-json.v1'),
    payload_digest_contract: z.literal('dmf.export-payload.sha256.v1'),
    export_id: uuidSchema,
    exported_at: instantSchema,
    created_by_build: buildMetadataSchema,
    payload: exportPayloadSchema,
});
const exportEnvelopeWireSchema = exportEnvelopeDraftSchema.extend({
    payload_sha256: sha256Schema,
});
export const importIssueSchema = z.strictObject({
    severity: z.enum(['warning', 'conflict', 'error']),
    code: z.enum([
        'schema_unsupported',
        'checksum_mismatch',
        'duplicate_id',
        'revision_conflict',
        'epoch_mismatch',
        'missing_reference',
        'invalid_record',
    ]),
    entity_type: z.string().min(1).max(128).nullable(),
    entity_id: uuidSchema.nullable(),
    message_key: z.string().regex(/^[a-z0-9_.-]+$/),
});
export const importPreviewSchema = z.strictObject({
    source_export_id: uuidSchema,
    source_export_sha256: sha256Schema,
    supported_schema_version: semverSchema,
    counts: z.strictObject({
        shifts: z.number().int().safe().nonnegative(),
        trip_segments: z.number().int().safe().nonnegative(),
        trip_revisions: z.number().int().safe().nonnegative(),
        vehicles: z.number().int().safe().nonnegative(),
        route_variants: z.number().int().safe().nonnegative(),
        route_templates: z.number().int().safe().nonnegative(),
        user_defined_locations: z.number().int().safe().nonnegative(),
        location_merge_decisions: z.number().int().safe().nonnegative(),
    }),
    duplicate_entity_ids: z.array(uuidSchema).max(1_000_000),
    issues: z.array(importIssueSchema).max(1_000_000),
    active_data_unchanged: z.literal(true),
});
export const restoreCheckpointSchema = immutableCheckpointSchema;
export const externalRescueEvidenceSchema = z
    .strictObject({
    initially_exported_sha256: sha256Schema,
    reselected_file_sha256: sha256Schema,
    reselected_and_verified_at: instantSchema,
})
    .superRefine((value, context) => {
    if (value.initially_exported_sha256 !== value.reselected_file_sha256) {
        context.addIssue({
            code: 'custom',
            path: ['reselected_file_sha256'],
            message: 'external rescue file must be reselected and match the original checksum',
        });
    }
});
const importPlanBaseSchema = z.strictObject({
    contract_version: z.literal('import_plan.v1'),
    import_plan_id: uuidSchema,
    source_export_id: uuidSchema,
    source_export_sha256: sha256Schema,
    source_dataset_epoch: uuidSchema,
    target_dataset_epoch: uuidSchema,
    prepared_at: instantSchema,
    preview: importPreviewSchema,
    explicit_user_confirmation_at: instantSchema.nullable(),
});
export const mergeAncestryEvidenceSchema = z
    .strictObject({
    ancestry_evidence_id: uuidSchema,
    entity_type: syncEntityTypeSchema,
    entity_id: uuidSchema,
    existing_revision: z.number().int().safe().nonnegative(),
    incoming_revision: z.number().int().safe().nonnegative(),
    relationship: z.enum([
        'existing_is_ancestor_of_incoming',
        'incoming_is_ancestor_of_existing',
        'common_ancestor_only',
    ]),
    common_ancestor_revision: z.number().int().safe().nonnegative().nullable(),
    evidence_kind: z.literal('verified_revision_history_chain.v1'),
    evidence_sha256: sha256Schema,
    verified_at: instantSchema,
})
    .superRefine((value, context) => {
    if (value.relationship === 'existing_is_ancestor_of_incoming' &&
        value.incoming_revision <= value.existing_revision) {
        context.addIssue({
            code: 'custom',
            path: ['incoming_revision'],
            message: 'an incoming descendant must have a newer revision than its existing ancestor',
        });
    }
    if (value.relationship === 'incoming_is_ancestor_of_existing' &&
        value.existing_revision <= value.incoming_revision) {
        context.addIssue({
            code: 'custom',
            path: ['existing_revision'],
            message: 'an existing descendant must have a newer revision than its incoming ancestor',
        });
    }
    if ((value.relationship === 'common_ancestor_only') !==
        (value.common_ancestor_revision !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['common_ancestor_revision'],
            message: 'only a common-ancestor relationship carries common_ancestor_revision',
        });
    }
    if (value.common_ancestor_revision !== null &&
        value.common_ancestor_revision >= Math.min(value.existing_revision, value.incoming_revision)) {
        context.addIssue({
            code: 'custom',
            path: ['common_ancestor_revision'],
            message: 'a common ancestor must precede both divergent revisions',
        });
    }
});
export const mergeLineageContextSchema = z
    .strictObject({
    contract_version: z.literal('merge_lineage_context.v1'),
    source_dataset_epoch: uuidSchema,
    target_dataset_epoch: uuidSchema,
    ancestry_evidence: z.array(mergeAncestryEvidenceSchema).max(1_000_000),
})
    .superRefine((value, context) => {
    const evidenceIds = value.ancestry_evidence.map((evidence) => evidence.ancestry_evidence_id);
    const entityKeys = value.ancestry_evidence.map((evidence) => `${evidence.entity_type}\u0000${evidence.entity_id}`);
    if (new Set(evidenceIds).size !== evidenceIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['ancestry_evidence'],
            message: 'ancestry evidence IDs must be unique',
        });
    }
    if (new Set(entityKeys).size !== entityKeys.length) {
        context.addIssue({
            code: 'custom',
            path: ['ancestry_evidence'],
            message: 'each merge entity may have at most one frozen ancestry proof',
        });
    }
});
export const mergeImportPlanSchema = importPlanBaseSchema
    .extend({
    mode: z.literal('merge'),
    execution_scope: z.enum(['local_profile', 'synchronized_account']),
    may_prepare_offline: z.literal(true),
    preserves_target_dataset_epoch: z.literal(true),
    conflict_policy: z.literal('visible_user_resolution'),
    lineage_context: mergeLineageContextSchema,
    idempotency_namespace_sha256: sha256Schema,
    generated_operations: z.array(pendingSyncOperationSchema).max(1_000_000),
})
    .superRefine((value, context) => {
    if (value.preview.source_export_id !== value.source_export_id ||
        value.preview.source_export_sha256 !== value.source_export_sha256) {
        context.addIssue({
            code: 'custom',
            path: ['preview'],
            message: 'preview must belong to the selected and verified source export',
        });
    }
    validateImportConfirmationTime(value, context);
    if (value.lineage_context.source_dataset_epoch !== value.source_dataset_epoch ||
        value.lineage_context.target_dataset_epoch !== value.target_dataset_epoch) {
        context.addIssue({
            code: 'custom',
            path: ['lineage_context'],
            message: 'merge lineage evidence must bind the exact source and target dataset epochs',
        });
    }
    value.lineage_context.ancestry_evidence.forEach((evidence, index) => {
        if (Date.parse(evidence.verified_at) > Date.parse(value.prepared_at)) {
            context.addIssue({
                code: 'custom',
                path: ['lineage_context', 'ancestry_evidence', index, 'verified_at'],
                message: 'merge ancestry evidence must exist before the frozen import plan',
            });
        }
    });
    const operationIds = new Set();
    const idempotencyKeys = new Set();
    const deviceSequences = new Set();
    value.generated_operations.forEach((operation, index) => {
        if (operation.operation_origin !== 'json_merge_import' ||
            operation.dataset_epoch !== value.target_dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['generated_operations', index],
                message: 'merge operations must be ordinary idempotent changes with import origin in the target epoch',
            });
        }
        const deviceSequence = `${operation.device_id}\u0000${operation.local_sequence}`;
        if (operationIds.has(operation.operation_id) ||
            idempotencyKeys.has(operation.idempotency_key_sha256) ||
            deviceSequences.has(deviceSequence)) {
            context.addIssue({
                code: 'custom',
                path: ['generated_operations', index],
                message: 'generated merge operations must have unique IDs, keys, and device sequences',
            });
        }
        operationIds.add(operation.operation_id);
        idempotencyKeys.add(operation.idempotency_key_sha256);
        deviceSequences.add(deviceSequence);
    });
});
export const replaceImportPlanSchema = importPlanBaseSchema
    .extend({
    mode: z.literal('replace'),
    execution_scope: z.enum(['local_profile', 'synchronized_account']),
    target_account_binding: z.enum(['unbound', 'bound']),
    connectivity: z.enum(['offline', 'online']),
    restore_checkpoint: restoreCheckpointSchema.nullable(),
    replacement_dataset_epoch: uuidSchema,
    invalidate_previous_outbox: z.literal(true),
    force_other_devices_full_resync: z.boolean(),
    external_rescue_evidence: externalRescueEvidenceSchema.nullable(),
})
    .superRefine((value, context) => {
    validateImportConfirmationTime(value, context);
    if (value.replacement_dataset_epoch === value.target_dataset_epoch ||
        value.replacement_dataset_epoch === value.source_dataset_epoch) {
        context.addIssue({
            code: 'custom',
            path: ['replacement_dataset_epoch'],
            message: 'authoritative replace must create a fresh dataset_epoch',
        });
    }
    if (value.preview.source_export_id !== value.source_export_id ||
        value.preview.source_export_sha256 !== value.source_export_sha256) {
        context.addIssue({
            code: 'custom',
            path: ['preview'],
            message: 'preview must belong to the selected and verified source export',
        });
    }
    if (value.restore_checkpoint) {
        if (value.restore_checkpoint.dataset_epoch !== value.target_dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['restore_checkpoint', 'dataset_epoch'],
                message: 'restore checkpoint must cover the active target epoch',
            });
        }
        if (Date.parse(value.restore_checkpoint.created_at) > Date.parse(value.prepared_at)) {
            context.addIssue({
                code: 'custom',
                path: ['restore_checkpoint', 'created_at'],
                message: 'checkpoint must exist before the replacement is prepared',
            });
        }
    }
    if (value.execution_scope === 'synchronized_account') {
        if (value.target_account_binding !== 'bound') {
            context.addIssue({
                code: 'custom',
                path: ['target_account_binding'],
                message: 'synchronized replacement requires a bound account',
            });
        }
        if (value.restore_checkpoint !== null &&
            value.restore_checkpoint.checkpoint_kind !== 'server_restore_checkpoint') {
            context.addIssue({
                code: 'custom',
                path: ['restore_checkpoint'],
                message: 'cloud replace requires a validated server checkpoint',
            });
        }
        if (!value.force_other_devices_full_resync) {
            context.addIssue({
                code: 'custom',
                path: ['force_other_devices_full_resync'],
                message: 'cloud replace must force every other device to full resync',
            });
        }
    }
    else {
        if (value.target_account_binding !== 'unbound') {
            context.addIssue({
                code: 'custom',
                path: ['target_account_binding'],
                message: 'local-only replacement is permitted only for an unbound profile',
            });
        }
        if (value.restore_checkpoint !== null &&
            value.restore_checkpoint.checkpoint_kind !== 'local_immutable_outside_active_db') {
            context.addIssue({
                code: 'custom',
                path: ['restore_checkpoint'],
                message: 'local replace requires an immutable checkpoint outside the active database',
            });
        }
        if (value.force_other_devices_full_resync) {
            context.addIssue({
                code: 'custom',
                path: ['force_other_devices_full_resync'],
                message: 'local-only replace has no other registered devices to invalidate',
            });
        }
    }
    if (value.external_rescue_evidence !== null &&
        value.external_rescue_evidence.initially_exported_sha256 !== value.source_export_sha256) {
        context.addIssue({
            code: 'custom',
            path: ['external_rescue_evidence'],
            message: 'external rescue evidence must cover the selected source export',
        });
    }
});
export const importPlanSchema = z.union([mergeImportPlanSchema, replaceImportPlanSchema]);
export const importJobSchema = z
    .strictObject({
    import_job_id: uuidSchema,
    source_export_sha256: sha256Schema,
    status: z.enum([
        'validating',
        'preview_ready',
        'awaiting_confirmation',
        'executing',
        'completed',
        'failed',
    ]),
    plan: importPlanSchema.nullable(),
    created_at: instantSchema,
    updated_at: instantSchema,
    failure_code: z
        .string()
        .regex(/^[a-z0-9_.-]+$/)
        .nullable(),
})
    .superRefine((value, context) => {
    if (Date.parse(value.updated_at) < Date.parse(value.created_at)) {
        context.addIssue({
            code: 'custom',
            path: ['updated_at'],
            message: 'import job update cannot precede creation',
        });
    }
    if ((value.status === 'failed') !== (value.failure_code !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['failure_code'],
            message: 'failure_code must be present exactly for a failed import job',
        });
    }
    if (['awaiting_confirmation', 'executing', 'completed'].includes(value.status) &&
        value.plan === null) {
        context.addIssue({
            code: 'custom',
            path: ['plan'],
            message: 'an import job at or beyond confirmation requires its frozen plan',
        });
    }
});
export const importExecutionDecisionSchema = z.strictObject({
    executable: z.boolean(),
    reason: z.enum([
        'ready',
        'validation_errors',
        'confirmation_required',
        'checkpoint_required',
        'online_required',
    ]),
});
export function evaluateImportExecution(planInput) {
    const plan = importPlanSchema.parse(planInput);
    if (plan.preview.issues.some((issue) => issue.severity === 'error')) {
        return { executable: false, reason: 'validation_errors' };
    }
    if (plan.explicit_user_confirmation_at === null) {
        return { executable: false, reason: 'confirmation_required' };
    }
    if (plan.mode === 'replace') {
        if (plan.restore_checkpoint === null) {
            return { executable: false, reason: 'checkpoint_required' };
        }
        if (plan.execution_scope === 'synchronized_account' && plan.connectivity !== 'online') {
            return { executable: false, reason: 'online_required' };
        }
    }
    return { executable: true, reason: 'ready' };
}
export function exportPayloadCanonicalJson(payloadInput) {
    return canonicalJsonStringify(exportPayloadSchema.parse(payloadInput));
}
export function createExportEnvelope(input) {
    const draft = exportEnvelopeDraftSchema.parse(input);
    return exportEnvelopeWireSchema.parse({
        ...draft,
        payload_sha256: sha256CanonicalJson('dmf.export-payload.sha256.v1', draft.payload),
    });
}
function parseExportEnvelopeObject(input) {
    const envelope = exportEnvelopeWireSchema.parse(input);
    const computedPayloadSha256 = sha256CanonicalJson('dmf.export-payload.sha256.v1', envelope.payload);
    if (envelope.payload_sha256 !== computedPayloadSha256) {
        throw new Error('export payload checksum mismatch');
    }
    return envelope;
}
export function createExportEnvelopeFile(input) {
    const envelope = parseExportEnvelopeObject(input);
    const canonicalJson = canonicalJsonStringify(envelope);
    const bytes = new TextEncoder().encode(canonicalJson);
    return {
        canonical_json: canonicalJson,
        envelope,
        whole_file_sha256: sha256HexBytes(bytes),
    };
}
/**
 * The only public parser for imported backup-file bytes. Fatal UTF-8 decode,
 * strict schema validation, payload checksum, canonical byte comparison, and
 * the whole-file digest are one inseparable operation.
 */
export function parseExportEnvelopeFile(input) {
    const bytes = input instanceof Uint8Array
        ? new Uint8Array(input.buffer, input.byteOffset, input.byteLength)
        : new Uint8Array(input);
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    const parsed = JSON.parse(decoded);
    const envelope = parseExportEnvelopeObject(parsed);
    const canonicalJson = canonicalJsonStringify(envelope);
    const canonicalBytes = new TextEncoder().encode(canonicalJson);
    if (!bytesEqual(bytes, canonicalBytes)) {
        throw new Error('export file bytes are not canonical UTF-8 JSON');
    }
    return {
        canonical_json: canonicalJson,
        envelope,
        whole_file_sha256: sha256HexBytes(bytes),
    };
}
function bytesEqual(left, right) {
    if (left.byteLength !== right.byteLength) {
        return false;
    }
    for (let index = 0; index < left.byteLength; index += 1) {
        if (left[index] !== right[index]) {
            return false;
        }
    }
    return true;
}
const mergeRecordBaseShape = {
    entity_id: uuidSchema,
    revision: z.number().int().safe().nonnegative(),
};
export const mergeCandidateRecordSchema = z
    .discriminatedUnion('entity_type', [
    z.strictObject({
        ...mergeRecordBaseShape,
        entity_type: z.literal('shift_aggregate'),
        value: shiftAggregateSchema,
    }),
    z.strictObject({
        ...mergeRecordBaseShape,
        entity_type: z.literal('vehicle'),
        value: vehicleSchema,
    }),
    z.strictObject({
        ...mergeRecordBaseShape,
        entity_type: z.literal('route_variant'),
        value: routeVariantSchema,
    }),
    z.strictObject({
        ...mergeRecordBaseShape,
        entity_type: z.literal('route_template'),
        value: routeTemplateSchema,
    }),
    z.strictObject({
        ...mergeRecordBaseShape,
        entity_type: z.literal('user_defined_location'),
        value: locationSchema,
    }),
    z.strictObject({
        ...mergeRecordBaseShape,
        entity_type: z.literal('location_merge_decision'),
        value: locationMergeDecisionSchema,
    }),
    z.strictObject({
        ...mergeRecordBaseShape,
        entity_type: z.literal('user_preferences'),
        value: userPreferencesSchema,
    }),
])
    .superRefine((record, context) => {
    const identity = (() => {
        switch (record.entity_type) {
            case 'shift_aggregate':
                return {
                    entity_id: record.value.shift.shift_id,
                    revision: record.value.aggregate_revision,
                };
            case 'vehicle':
                return { entity_id: record.value.vehicle_id, revision: record.value.metadata.revision };
            case 'route_variant':
                return {
                    entity_id: record.value.route_variant_id,
                    revision: record.value.metadata.revision,
                };
            case 'route_template':
                return {
                    entity_id: record.value.route_template_id,
                    revision: record.value.metadata.revision,
                };
            case 'user_defined_location':
                return { entity_id: record.value.location_id, revision: record.value.metadata.revision };
            case 'location_merge_decision':
                return { entity_id: record.value.decision_id, revision: record.value.metadata.revision };
            case 'user_preferences':
                return {
                    entity_id: record.value.user_preferences_id,
                    revision: record.value.metadata.revision,
                };
        }
    })();
    if (identity.entity_id !== record.entity_id || identity.revision !== record.revision) {
        context.addIssue({
            code: 'custom',
            message: 'merge record identity and revision must match the typed value root',
        });
    }
});
export const mergeRecordDecisionSchema = z
    .strictObject({
    source_dataset_epoch: uuidSchema,
    target_dataset_epoch: uuidSchema,
    entity_type: syncEntityTypeSchema,
    entity_id: uuidSchema,
    incoming_revision: z.number().int().safe().nonnegative(),
    existing_revision: z.number().int().safe().nonnegative().nullable(),
    disposition: z.enum(['apply', 'duplicate', 'stale', 'conflict']),
    lineage_basis: z.enum([
        'new_entity',
        'identical_record',
        'same_epoch_revision_order',
        'same_epoch_equal_revision_divergence',
        'verified_existing_ancestor',
        'verified_incoming_ancestor',
        'verified_common_ancestor_only',
        'cross_epoch_unproven',
    ]),
    ancestry_evidence_id: uuidSchema.nullable(),
})
    .superRefine((value, context) => {
    const sameEpoch = value.source_dataset_epoch === value.target_dataset_epoch;
    const expected = (() => {
        switch (value.lineage_basis) {
            case 'new_entity':
                return {
                    disposition: 'apply',
                    existing: false,
                    sameEpoch: null,
                    evidence: false,
                };
            case 'identical_record':
                return {
                    disposition: 'duplicate',
                    existing: true,
                    sameEpoch: null,
                    evidence: false,
                };
            case 'same_epoch_revision_order':
                return {
                    disposition: value.existing_revision !== null && value.incoming_revision > value.existing_revision
                        ? 'apply'
                        : 'stale',
                    existing: true,
                    sameEpoch: true,
                    evidence: false,
                };
            case 'same_epoch_equal_revision_divergence':
                return {
                    disposition: 'conflict',
                    existing: true,
                    sameEpoch: true,
                    evidence: false,
                };
            case 'verified_existing_ancestor':
                return {
                    disposition: 'apply',
                    existing: true,
                    sameEpoch: false,
                    evidence: true,
                };
            case 'verified_incoming_ancestor':
                return {
                    disposition: 'stale',
                    existing: true,
                    sameEpoch: false,
                    evidence: true,
                };
            case 'verified_common_ancestor_only':
                return {
                    disposition: 'conflict',
                    existing: true,
                    sameEpoch: false,
                    evidence: true,
                };
            case 'cross_epoch_unproven':
                return {
                    disposition: 'conflict',
                    existing: true,
                    sameEpoch: false,
                    evidence: false,
                };
        }
    })();
    if (expected.disposition !== value.disposition) {
        context.addIssue({
            code: 'custom',
            path: ['disposition'],
            message: 'lineage basis and disposition disagree',
        });
    }
    if (expected.existing !== (value.existing_revision !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['existing_revision'],
            message: 'lineage basis and existing revision disagree',
        });
    }
    if (expected.sameEpoch !== null && expected.sameEpoch !== sameEpoch) {
        context.addIssue({
            code: 'custom',
            path: ['target_dataset_epoch'],
            message: 'lineage basis and dataset epochs disagree',
        });
    }
    if (expected.evidence !== (value.ancestry_evidence_id !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['ancestry_evidence_id'],
            message: 'lineage basis and ancestry evidence disagree',
        });
    }
    if (value.lineage_basis === 'same_epoch_revision_order' &&
        value.existing_revision === value.incoming_revision) {
        context.addIssue({
            code: 'custom',
            path: ['lineage_basis'],
            message: 'equal revisions cannot use revision-order lineage',
        });
    }
    if ((value.lineage_basis === 'identical_record' ||
        value.lineage_basis === 'same_epoch_equal_revision_divergence') &&
        value.existing_revision !== value.incoming_revision) {
        context.addIssue({
            code: 'custom',
            path: ['incoming_revision'],
            message: 'this lineage basis requires equal existing and incoming revisions',
        });
    }
    if (value.lineage_basis === 'verified_existing_ancestor' &&
        value.existing_revision !== null &&
        value.incoming_revision <= value.existing_revision) {
        context.addIssue({
            code: 'custom',
            path: ['incoming_revision'],
            message: 'a verified incoming descendant must have a newer revision',
        });
    }
    if (value.lineage_basis === 'verified_incoming_ancestor' &&
        value.existing_revision !== null &&
        value.incoming_revision >= value.existing_revision) {
        context.addIssue({
            code: 'custom',
            path: ['incoming_revision'],
            message: 'a verified incoming ancestor must have an older revision',
        });
    }
});
export function createMergeRecordDecisions(lineageInput, existingInput, incomingInput) {
    const lineage = mergeLineageContextSchema.parse(lineageInput);
    const existing = z.array(mergeCandidateRecordSchema).parse(existingInput);
    const incoming = z.array(mergeCandidateRecordSchema).parse(incomingInput);
    assertUniqueMergeRecords(existing, 'existing');
    assertUniqueMergeRecords(incoming, 'incoming');
    const existingByKey = new Map(existing.map((record) => [`${record.entity_type}\u0000${record.entity_id}`, record]));
    const evidenceByKey = new Map(lineage.ancestry_evidence.map((evidence) => [
        `${evidence.entity_type}\u0000${evidence.entity_id}`,
        evidence,
    ]));
    return incoming
        .map((record) => {
        const entityKey = `${record.entity_type}\u0000${record.entity_id}`;
        const current = existingByKey.get(entityKey);
        const evidence = evidenceByKey.get(entityKey);
        let disposition;
        let lineageBasis;
        let ancestryEvidenceId = null;
        if (!current) {
            disposition = 'apply';
            lineageBasis = 'new_entity';
        }
        else if (canonicalJsonStringify(current.value) === canonicalJsonStringify(record.value)) {
            disposition = 'duplicate';
            lineageBasis = 'identical_record';
        }
        else if (lineage.source_dataset_epoch === lineage.target_dataset_epoch) {
            if (record.revision < current.revision) {
                disposition = 'stale';
                lineageBasis = 'same_epoch_revision_order';
            }
            else if (record.revision === current.revision) {
                disposition = 'conflict';
                lineageBasis = 'same_epoch_equal_revision_divergence';
            }
            else {
                disposition = 'apply';
                lineageBasis = 'same_epoch_revision_order';
            }
        }
        else if (evidence !== undefined &&
            evidence.existing_revision === current.revision &&
            evidence.incoming_revision === record.revision) {
            ancestryEvidenceId = evidence.ancestry_evidence_id;
            if (evidence.relationship === 'existing_is_ancestor_of_incoming') {
                disposition = 'apply';
                lineageBasis = 'verified_existing_ancestor';
            }
            else if (evidence.relationship === 'incoming_is_ancestor_of_existing') {
                disposition = 'stale';
                lineageBasis = 'verified_incoming_ancestor';
            }
            else {
                disposition = 'conflict';
                lineageBasis = 'verified_common_ancestor_only';
            }
        }
        else {
            disposition = 'conflict';
            lineageBasis = 'cross_epoch_unproven';
        }
        return mergeRecordDecisionSchema.parse({
            source_dataset_epoch: lineage.source_dataset_epoch,
            target_dataset_epoch: lineage.target_dataset_epoch,
            entity_type: record.entity_type,
            entity_id: record.entity_id,
            incoming_revision: record.revision,
            existing_revision: current?.revision ?? null,
            disposition,
            lineage_basis: lineageBasis,
            ancestry_evidence_id: ancestryEvidenceId,
        });
    })
        .sort((left, right) => left.entity_type.localeCompare(right.entity_type) ||
        left.entity_id.localeCompare(right.entity_id));
}
export function replacementDatasetEpoch(planInput) {
    const plan = replaceImportPlanSchema.parse(planInput);
    if (!evaluateImportExecution(plan).executable || plan.restore_checkpoint === null) {
        throw new Error('replacement plan is not executable');
    }
    return datasetEpochSchema.parse({
        dataset_epoch: plan.replacement_dataset_epoch,
        created_at: plan.prepared_at,
        predecessor_epoch: plan.target_dataset_epoch,
        reason: 'authoritative_replace',
        restore_checkpoint_id: plan.restore_checkpoint.checkpoint_id,
    });
}
function assertUniqueMergeRecords(records, label) {
    const keys = records.map((record) => `${record.entity_type}\u0000${record.entity_id}`);
    if (new Set(keys).size !== keys.length) {
        throw new Error(`${label} merge records contain a duplicate entity key`);
    }
}
function requireUnique(values, path, context) {
    if (new Set(values).size !== values.length) {
        context.addIssue({ code: 'custom', path: [path], message: `${path} IDs must be unique` });
    }
}
function tripSegmentUsesExpandedRouteWire(segment) {
    return Object.hasOwn(segment, 'route_draft_snapshot');
}
function routeUsesExpandedLearningWire(route) {
    return route.default_for_context && route.explicit_confirmation_count < 3;
}
function validateTombstoneEpoch(tombstone, datasetEpoch, path, context) {
    if (tombstone !== null && tombstone.dataset_epoch !== datasetEpoch) {
        context.addIssue({
            code: 'custom',
            path,
            message: 'exported tombstone must belong to the payload dataset epoch',
        });
    }
}
function validateImportConfirmationTime(value, context) {
    if (value.explicit_user_confirmation_at !== null &&
        Date.parse(value.explicit_user_confirmation_at) < Date.parse(value.prepared_at)) {
        context.addIssue({
            code: 'custom',
            path: ['explicit_user_confirmation_at'],
            message: 'import confirmation must not precede the frozen preview and plan',
        });
    }
}
//# sourceMappingURL=import-export.js.map